/**
 * 实时通信 Store
 *
 * 管理内容：
 * - WebSocket 连接状态
 * - 教师端：所有学员的实时状态（StudentLiveStatus 字典）
 * - 学员端：收到的教师指令
 * - 实时操作流（教师端展示用，最近 100 条）
 *
 * 教师端使用：
 *   realtimeStore.subscribe(classId)
 *   const students = useRealtimeStore(s => s.students)
 *
 * 学员端使用：
 *   realtimeStore.connect(token)
 *   const teacherCommand = useRealtimeStore(s => s.lastTeacherCommand)
 */

import { create } from 'zustand';
import type { ServerMessage, StudentLiveStatus } from '@/services/realtime-types';
import { getRealtimeClient, destroyRealtimeClient } from '@/services/realtime';

// 操作流最大缓存条数
const MAX_ACTION_LOG = 100;

interface ActionLogEntry {
  userId: string;
  display_name: string;
  action: string;
  isCorrect: boolean;
  timestamp: number;
}

interface RealtimeState {
  // 连接状态
  status: 'disconnected' | 'connecting' | 'connected';
  // 教师端：学员实时状态字典 { userId → status }
  students: Record<string, StudentLiveStatus>;
  // 教师端：最近 N 条操作流（按时间正序）
  actionLog: ActionLogEntry[];
  // 学员端：最近一条教师指令
  lastTeacherCommand: { command: string; message?: string; timestamp: number } | null;
  // 当前订阅的班级 ID
  subscribedClassId: string | null;

  // Actions
  /** 建立 WebSocket 连接（需要 auth token） */
  connect: (token: string) => void;
  /** 关闭连接 */
  disconnect: () => void;
  /** 教师订阅班级实时数据 */
  subscribeClass: (classId: string) => void;
  /** 清空学员状态（离开监控页时调用） */
  clearStudents: () => void;
  /** 内部：处理收到的服务器消息 */
  handleMessage: (msg: ServerMessage) => void;
}

export const useRealtimeStore = create<RealtimeState>((set, get) => ({
  status: 'disconnected',
  students: {},
  actionLog: [],
  lastTeacherCommand: null,
  subscribedClassId: null,

  connect: (token) => {
    const client = getRealtimeClient(token);
    if (!client) return;

    // 注册消息处理器（只需注册一次）
    client.onAny((msg) => get().handleMessage(msg));

    // 更新连接状态
    const checkStatus = () => set({ status: client.status });
    checkStatus();
    // 每 500ms 轮询连接状态（因为 RealtimeClient 的 status 是普通属性，非响应式）
    const statusTimer = setInterval(checkStatus, 500);

    if (client.status !== 'connected') {
      client.connect();
    }

    // 清理函数挂到 store 上，disconnect 时清理
    (get() as RealtimeState & { _statusTimer?: ReturnType<typeof setInterval> })._statusTimer = statusTimer;
  },

  disconnect: () => {
    destroyRealtimeClient();
    const state = get() as RealtimeState & { _statusTimer?: ReturnType<typeof setInterval> };
    if (state._statusTimer) {
      clearInterval(state._statusTimer);
      state._statusTimer = undefined;
    }
    set({ status: 'disconnected', students: {}, actionLog: [], subscribedClassId: null });
  },

  subscribeClass: (classId) => {
    const client = getRealtimeClient();
    if (!client) return;
    set({ subscribedClassId: classId, students: {}, actionLog: [] });
    client.send({ type: 'subscribe', role: 'teacher', classId });
  },

  clearStudents: () => {
    set({ students: {}, actionLog: [], subscribedClassId: null });
  },

  handleMessage: (msg) => {
    switch (msg.type) {
      case 'class:sync': {
        // 全量同步：把数组转为字典
        const studentsMap: Record<string, StudentLiveStatus> = {};
        for (const s of msg.students) {
          studentsMap[s.userId] = s;
        }
        set({ students: studentsMap });
        break;
      }
      case 'student:start': {
        set((state) => ({
          students: {
            ...state.students,
            [msg.userId]: {
              userId: msg.userId,
              display_name: msg.display_name,
              isDrilling: true,
              sceneId: msg.sceneId,
              faultName: msg.faultName,
              difficulty: msg.difficulty,
              startedAt: msg.timestamp,
              lastUpdate: msg.timestamp,
            },
          },
        }));
        break;
      }
      case 'student:state': {
        set((state) => {
          const prev = state.students[msg.userId];
          if (!prev) {
            // 收到状态但之前没有 start 消息，补一条
            return {
              students: {
                ...state.students,
                [msg.userId]: {
                  userId: msg.userId,
                  display_name: msg.display_name,
                  isDrilling: true,
                  equipmentSnapshot: msg.equipmentSnapshot,
                  divergingKeys: msg.divergingKeys,
                  lastUpdate: Date.now(),
                },
              },
            };
          }
          return {
            students: {
              ...state.students,
              [msg.userId]: {
                ...prev,
                display_name: msg.display_name,
                equipmentSnapshot: msg.equipmentSnapshot,
                divergingKeys: msg.divergingKeys,
                lastUpdate: Date.now(),
              },
            },
          };
        });
        break;
      }
      case 'student:action': {
        set((state) => {
          const prev = state.students[msg.userId];
          const newEntry: ActionLogEntry = {
            userId: msg.userId,
            display_name: msg.display_name,
            action: msg.action,
            isCorrect: msg.isCorrect,
            timestamp: msg.timestamp,
          };
          // 操作流保留最近 MAX_ACTION_LOG 条
          const newLog = [...state.actionLog, newEntry].slice(-MAX_ACTION_LOG);
          return {
            actionLog: newLog,
            students: prev
              ? {
                  ...state.students,
                  [msg.userId]: {
                    ...prev,
                    lastAction: newEntry,
                    lastUpdate: Date.now(),
                  },
                }
              : state.students,
          };
        });
        break;
      }
      case 'student:end': {
        set((state) => {
          const prev = state.students[msg.userId];
          if (!prev) return state;
          return {
            students: {
              ...state.students,
              [msg.userId]: {
                ...prev,
                isDrilling: false,
                lastUpdate: Date.now(),
              },
            },
          };
        });
        break;
      }
      case 'teacher:command': {
        set({
          lastTeacherCommand: {
            command: msg.command,
            message: msg.message,
            timestamp: msg.timestamp,
          },
        });
        break;
      }
      case 'connected': {
        set({ status: 'connected' });
        break;
      }
      case 'pong': {
        // 心跳响应，无需处理
        break;
      }
      default: {
        // 其他消息类型忽略
      }
    }
  },
}));
